import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { RootState } from '../../../store/store';
import {
    Box, Button, Checkbox, CircularProgress, Container,
    FormControlLabel, FormGroup, Radio, RadioGroup, Tooltip, Typography,
} from '@mui/material';
import CsvDateRangeDialog from '../../../components/CsvDateRangeDialog';
import DropdownMenu, { DropdownMenuItem } from '../../../components/DropdownMenu';
import { getCreateBookingOptionsForTenant } from '../../../config/bookingServiceMapping';
import { useGetAssetsAvailabilityDataQuery } from '../../../api/booking/bookingApiSlice';
import FilterListIcon from '@mui/icons-material/FilterList';
import BookingTable from './BookingTable';
import type { Booking, BookingTableEntry } from './types';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { useFetchBooking } from '../../../flows/fetchBookingsFlow';
import { useLocation, useNavigate } from 'react-router';
import { makeRentalBookingDetailsUrl, makeSeriesBookingDetailsUrl, paths } from '../../../routes';
import CustomButton from '../../../components/CustomButton';
import { useTranslation } from 'react-i18next';
import { setBreadcrumbPathAndBookingNumber } from '../../../store/slices/bookingSlice';
import { enqueueErrorSnackbar, enqueueWarningSnackbar } from '../../../action/snackbarCommands';
import Permission from '../../../common/permissions';
import { makeHasPermissionSelector, tenantSelector } from '../../../selector/authenticationSelector';
import { BookingStatusType } from '../../../components/StatusBadge';
import PageHeadline from '../../../components/PageHeadline';
import PageSubtitle from '../../../components/PageSubtitle';
import PageHeader from '../../../components/PageHeader';
import SearchBar from '../../../components/SearchBar';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { exportBookingsToCSV } from '../../../utils/csvExport';
import { buildBookingNumberLetterMap } from '../../../utils/bookingNumberLetters';
import { isOvertimeBooking } from '../../../utils/bookingFinishTiming';
import {
    APPROVAL_QUERY_PARAM,
    AWAITING_APPROVAL_QUERY_VALUE,
    isAwaitingApproval,
} from '../../../utils/bookingApproval';
import {
    bookingIsInLocker,
    buildLockerQuery,
    parseLockerQuery,
    tenantHasLockerColumn,
} from '../../../utils/lockerSearch';
import {
    DAY_FILTER_OPTIONS,
    DayFilter,
    bookingDayFactsOf,
    bookingIsOnDay,
    dayFilterKey,
    parseDayFilter,
} from '../../../utils/bookingDay';
import { AlertTriangle } from 'lucide-react';
import { isRoomFamily } from '../../../utils/useCases';
import BookingsBulkActions from './BookingsBulkActions';
import { selectedBookingsOf, toggleAllVisible, toggleSelected } from '../../../utils/bookingBulkActions';

// Must list every status the API can send. A status missing here is not
// merely unfilterable: `matchesStatus` reads `statusFilters[status]`, so an
// unknown value is `undefined` and the booking vanishes from the list the
// moment ANY box is unchecked — whichever status the operator was after.
// `overtime` was missing, so the boards that are actually out were exactly
// the ones that disappeared when you narrowed the list (#2417 F1).
const STATUS_KEYS: BookingStatusType[] = [
    'active', 'booked', 'canceled', 'reserved', 'finished',
    'incompleted', 'blocked', 'conflicted', 'expired', 'overtime',
];

interface BookingsPageProps {
    /** The Bookings menu's tab bar (`BookingsArea`), shown under the title. */
    viewTabs?: React.ReactNode;
}

const BookingsPage = ({ viewTabs }: BookingsPageProps = {}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const location = useLocation();
    const { bookings, loading, error: loadError } = useSelector((state: RootState) => state.bookingPage);
    // True once the first fetch for this mount has settled. The slice's
    // `loading` only flips true *inside* the fetch effect (which runs after the
    // first paint), so on remount there's a frame with stale-empty bookings and
    // loading=false — that's the "no bookings" flash seen right after deleting a
    // booking and returning to the list. Treat "not yet fetched" as loading too.
    const [hasFetched, setHasFetched] = useState(false);
    const hasCreateBookingsPermission = useSelector(makeHasPermissionSelector(Permission.createBookings()));
    // Bulk approve and cancel (#3016) are edits, so `edit:bookings`, as on the
    // detail pages. Export follows the export button: whoever sees the list.
    const hasEditBookingsPermission = useSelector(makeHasPermissionSelector(Permission.editBookings()));
    const config = useSelector((state: RootState) => state.authentication.config);
    const [showChecklist, setShowChecklist] = useState(false);
    const [filters, setFilters] = useState<{ [key: string]: boolean }>({});
    const useCases = config?.services;
    const selectedTenant = useSelector(tenantSelector);
    const [page, setPage] = useState(0);
    // Seeded from `?search=` so another screen can hand this page a filter —
    // the account detail view links here to show one customer's bookings
    // (`makeBookingsSearchUrl`). Read once as the initial value rather than
    // kept in sync: the operator must stay free to clear or edit the box, and
    // a controlled-from-URL search would fight them on every keystroke.
    //
    // `?door=` (makeBookingsForDoorUrl) seeds the same box with a locker query
    // instead — "Fach 11" — rather than a filter of its own: the operator then
    // sees exactly what is filtering the list, and can edit or clear it like
    // anything else they typed.
    const [searchParams] = useSearchParams();
    const [searchQuery, setSearchQuery] = useState<string>(() => {
        const door = searchParams.get('door');
        if (door) {
            return buildLockerQuery(t('bookings.bookings_table.locker_prefix'), door);
        }
        return searchParams.get('search') ?? '';
    });
    const [includeArchived, setIncludeArchived] = useState(false);
    // Overtime is not a status — a booking can be finished-late or still out
    // past its end — so it filters alongside the status set, not inside it.
    const [overtimeOnly, setOvertimeOnly] = useState(false);
    // M8 approval (#1809): also not a status — a reserved room booking is
    // either still awaiting approval or approved. Seeded once from
    // `?approval=awaiting`, the dashboard figure's link
    // (`makeBookingsAwaitingApprovalUrl`), and then the operator's to change.
    const [awaitingApprovalOnly, setAwaitingApprovalOnly] = useState<boolean>(
        () => searchParams.get(APPROVAL_QUERY_PARAM) === AWAITING_APPROVAL_QUERY_VALUE,
    );
    // #3016: "starts / ends today / tomorrow", seeded once from `?day=` +
    // `?dayEdge=` — the dashboard's Today / Tomorrow figures link here
    // (`makeBookingsOnDayUrl`) — and then the operator's to change. The rule is
    // `utils/bookingDay.ts`, the mirror of the one that counts the figure.
    const [dayFilter, setDayFilter] = useState<DayFilter | null>(() => parseDayFilter(searchParams));
    const hasRoomUseCase = (useCases ?? []).some((useCase: string) => isRoomFamily(useCase));
    const [exportLoading, setExportLoading] = useState(false);
    const [exportDialogOpen, setExportDialogOpen] = useState(false);
    const [exportDateFrom, setExportDateFrom] = useState('');
    const [exportDateTo, setExportDateTo] = useState('');
    const [statusFilters, setStatusFilters] = useState<Record<BookingStatusType | 'all', boolean>>({
        active: true,
        booked: true,
        canceled: true,
        finished: true,
        reserved: true,
        incompleted: true,
        blocked: true,
        conflicted: true,
        expired: true,
        overtime: true,
        all: true,
    });
    // Bulk actions (#3016): the ticked booking ids.
    const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(() => new Set());


    // Map filter selections to use cases for API calls
    const getUseCasesForAPI = () => {
        if (!filters || Object.keys(filters).length === 0) {
            return useCases ?? [''];
        }

        const selectedUseCases: string[] = [];

        if (filters['all']) {
            // If 'all' is selected, return all available use cases
            return useCases ?? [''];
        } else {
            // Add selected use cases
            Object.entries(filters).forEach(([key, value]) => {
                if (value && key !== 'all') {
                    selectedUseCases.push(key);
                }
            });
        }

        return selectedUseCases.length > 0 ? selectedUseCases : useCases ?? [''];
    };

    // The "e.g. Fach 11" hint only where Fächer exist. A room-rental tenant
    // has no lockers, so advertising the form there offers a concept the
    // operator cannot use. Same rule the locker column is rendered from, so
    // the hint and the column cannot disagree.
    const searchPlaceholder = tenantHasLockerColumn(useCases)
        ? t('bookings.bookings_table.search_with_locker')
        : t('bookings.bookings_table.search');

    const currentUseCases = useMemo(() => getUseCasesForAPI(), [filters, useCases]);
    const fetchBooking = useFetchBooking(currentUseCases, includeArchived);

    const SERVICE_LABELS: Record<string, string> = {
        apartment: t('bookings.bookings_table.filter_options.room_rental'),
        sup: t('bookings.bookings_table.filter_options.sup'),
        rental: t('bookings.bookings_table.filter_options.rental'),
        p2p: t('bookings.bookings_table.filter_options.p2p'),
        storage: t('bookings.bookings_table.filter_options.storage'),
        parcel: t('bookings.bookings_table.filter_options.parcel'),
        'venue-reservation': t('bookings.bookings_table.filter_options.venue_reservation'),
        venue: t('bookings.bookings_table.filter_options.venue_reservation'),
        hall: t('bookings.bookings_table.filter_options.hall'),
    };

    useEffect(() => {
        const fetchOrders = async () => {
            try {
                await fetchBooking();
            } finally {
                setHasFetched(true);
            }
        };
        fetchOrders();
    }, [config, location.pathname, currentUseCases, includeArchived]);

    const filteredBookings = useMemo(() => {
        const sortedBookings = [...bookings].sort((a, b) => {
            // const dateA = new Date(a.startDate).getTime();
            // const dateB = new Date(b.startDate).getTime();
            // const aIsActive = dateA <= currentTime && new Date(a.endDate).getTime() >= currentTime;
            // const bIsActive = dateB <= currentTime && new Date(b.endDate).getTime() >= currentTime;

            // if (aIsActive && !bIsActive) return -1;
            // if (!aIsActive && bIsActive) return 1;

            const createdAtA = new Date(a.createdAt).getTime();
            const createdAtB = new Date(b.createdAt).getTime();
            return createdAtB - createdAtA;
        });

        // Multi-order purchases share one booking number; show the display
        // letters (A/B/…) from the shared derivation so the list matches the
        // end-user app, the confirmation emails and the master list.
        const displayNumberByBookingId = buildBookingNumberLetterMap(
            sortedBookings,
            {
                getId: (booking) => booking.bookingId,
                getBookingNumber: (booking) => booking.bookingNumber,
            },
        );

        const differentiatedAndSortedBookings = sortedBookings.map((booking) => {
            const displayNumber = displayNumberByBookingId.get(booking.bookingId);
            return displayNumber
                ? { ...booking, bookingNumber: displayNumber }
                : booking;
        });

        const searchTerm = searchQuery.toLowerCase().trim();
        // "Fach 11" narrows to exactly locker 11 instead of running the term
        // through the free-text match, where a bare 11 also hits every booking
        // number containing 11 and buries the rows that were wanted.
        const lockerQuery = parseLockerQuery(searchQuery);

        const activeUseCaseFilters = Object.entries(filters)
            .filter(([key, value]) => key !== 'all' && value)
            .map(([key]) => key);
        if (activeUseCaseFilters.length === 0) {
            return {
                bookings: [],
                totalFilteredRows: 0,
            };
        }

        const isFilteringByUseCase = activeUseCaseFilters.length > 0;
        const enabledStatuses = STATUS_KEYS.filter((k) => statusFilters[k]);
        const isFilteringByStatus = enabledStatuses.length < STATUS_KEYS.length;
        // One timestamp for the whole pass, so two rows one millisecond apart
        // can't be judged against different "now"s.
        const now = Date.now();
        const nowDate = new Date(now);

        const filtered = differentiatedAndSortedBookings.filter((booking) => {
            const matchesSearch = lockerQuery !== null
                ? bookingIsInLocker(booking.door, lockerQuery)
                : !searchTerm ||
                booking.bookingNumber.toLowerCase().includes(searchTerm) ||
                booking.address.toLowerCase().includes(searchTerm) ||
                booking.senderFullname.toLowerCase().includes(searchTerm) ||
                (booking.senderEmail && booking.senderEmail.toLowerCase().includes(searchTerm)) || 
                booking.door?.toString().toLowerCase().includes(searchTerm);

            const matchesUseCase = !isFilteringByUseCase || activeUseCaseFilters.includes(booking.useCase);

            const bookingStatusKey = booking.bookingStatus.toLowerCase() as BookingStatusType;

            const matchesStatus = !isFilteringByStatus || statusFilters[bookingStatusKey];

            const matchesOvertime = !overtimeOnly || isOvertimeBooking(
                booking.bookingStatus,
                booking.finishedAtDate,
                booking.endDate,
                now,
            );

            const matchesApproval = !awaitingApprovalOnly || isAwaitingApproval(booking.approvalState);

            const matchesDay = !dayFilter || bookingIsOnDay(
                bookingDayFactsOf(booking),
                dayFilter.edge,
                dayFilter.preset,
                nowDate,
            );

            return matchesSearch && matchesUseCase && matchesStatus && matchesOvertime && matchesApproval
                && matchesDay;
        });


        return {
            bookings: filtered,
            totalFilteredRows: filtered.length,
        };
    }, [bookings, searchQuery, page, filters, statusFilters, overtimeOnly, awaitingApprovalOnly, dayFilter]);

    // A selection belongs to the list it was made in: a new filter, search,
    // archive toggle or tenant starts with none, so nothing ticked out of
    // sight is ever acted on (CLAUDE.md, "Reset State on Tenant / Context Switch").
    useEffect(() => {
        setSelectedIds(new Set());
    }, [searchQuery, filters, statusFilters, overtimeOnly, awaitingApprovalOnly, includeArchived, selectedTenant]);

    const selectedBookings = useMemo(
        () => selectedBookingsOf(filteredBookings.bookings, selectedIds),
        [filteredBookings.bookings, selectedIds],
    );

    const tableEntries: BookingTableEntry[] = useMemo(() => {
        const filtered = filteredBookings.bookings;
        const seriesMap = new Map<string, typeof filtered>();
        const singles: typeof filtered = [];

        for (const booking of filtered) {
            if (booking.seriesId) {
                const group = seriesMap.get(booking.seriesId);
                if (group) {
                    group.push(booking);
                } else {
                    seriesMap.set(booking.seriesId, [booking]);
                }
            } else {
                singles.push(booking);
            }
        }

        const entries: BookingTableEntry[] = [];

        // Build a position map: where would the first booking of each series appear in the original list?
        const positionMap = new Map<string, number>();
        filtered.forEach((b, i) => {
            if (b.seriesId && !positionMap.has(b.seriesId)) {
                positionMap.set(b.seriesId, i);
            }
        });

        // Interleave singles and series groups in original sort order
        const seriesEntries: Array<{ seriesId: string, bookings: Booking[], position: number }> = [];
        seriesMap.forEach((seriesBookings, seriesId) => {
            seriesEntries.push({
                seriesId,
                bookings: seriesBookings.sort((a: Booking, b: Booking) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime()),
                position: positionMap.get(seriesId) ?? 0,
            });
        });
        seriesEntries.sort((a, b) => a.position - b.position);

        let singleIdx = 0;
        let seriesIdx = 0;

        while (singleIdx < singles.length || seriesIdx < seriesEntries.length) {
            const nextSingle = singles[singleIdx];
            const nextSeries = seriesEntries[seriesIdx];

            if (!nextSeries) {
                entries.push({ type: 'single', booking: nextSingle });
                singleIdx++;
            } else if (!nextSingle) {
                entries.push({ type: 'series', seriesId: nextSeries.seriesId, bookings: nextSeries.bookings });
                seriesIdx++;
            } else {
                // Compare positions in original filtered array
                const singlePos = filtered.indexOf(nextSingle);
                if (singlePos < nextSeries.position) {
                    entries.push({ type: 'single', booking: nextSingle });
                    singleIdx++;
                } else {
                    entries.push({ type: 'series', seriesId: nextSeries.seriesId, bookings: nextSeries.bookings });
                    seriesIdx++;
                }
            }
        }

        return entries;
    }, [filteredBookings.bookings]);

    // Fetch assets so the create-booking dropdown only shows service
    // types the tenant actually has at least one asset for. A self-
    // service tenant who's only created an apartment shouldn't see
    // "Venue booking" in the dropdown.
    const now = useMemo(() => new Date(), []);
    const { data: assetAvailabilityData } = useGetAssetsAvailabilityDataQuery({
        data: {
            year: String(now.getFullYear()),
            month: String(now.getMonth() + 1),
        },
    });
    // Unanswered (loading or failed): no filter, as the dashboard's chooser does —
    // a failed read must not empty the create menu as if the tenant had no objects.
    const servicesWithAssets = useMemo(
        () => (assetAvailabilityData ? new Set(assetAvailabilityData.flatMap((a) => a.useCases ?? [])) : null),
        [assetAvailabilityData],
    );

    const createBookingMenuItems: DropdownMenuItem[] = useMemo(() => {
        if (!config?.services) return [];

        // Only include services where the tenant has ≥1 asset.
        const hasAsset = (s: string) => !servicesWithAssets || servicesWithAssets.has(s);
        const services = config.services.filter(hasAsset);
        const createBookingOptions = (config.createBookingOptions ?? []).filter(hasAsset);

        const bookingOptions = getCreateBookingOptionsForTenant(
            services,
            createBookingOptions,
        );

        const items: DropdownMenuItem[] = bookingOptions.map(option => ({
            id: option.id,
            label: t(option.translationKey),
            onClick: () => navigate(option.route),
        }));

        // Add blocker and series booking options only if the tenant has
        // at least one room-family asset — otherwise both flows have
        // nothing to operate on.
        const hasRoomFamilyAsset = (servicesWithAssets ? Array.from(servicesWithAssets) : config.services).some((s: string) => isRoomFamily(s));
        if (hasRoomFamilyAsset) {
            if (items.length > 0) {
                items[items.length - 1] = { ...items[items.length - 1], divider: true };
            }
            items.push({
                id: 'series-booking',
                label: t('bookings.create.series_booking.title'),
                onClick: () => navigate(paths.createSeriesBooking),
            });
            items.push({
                id: 'blocker',
                label: t('bookings.create.blocker.title'),
                onClick: () => navigate(paths.createBlocker),
            });
        }

        return items;
    }, [config, t, navigate, servicesWithAssets]);

    const noAssetForBooking = createBookingMenuItems.length === 0;
    useEffect(() => {
        if (useCases && Object.keys(filters).length === 0) {
            const initialFilters: { [key: string]: boolean } = {};
            useCases.forEach((useCase: string) => {
                initialFilters[useCase] = true;
            });
            if (useCases.length > 1) {
                initialFilters['all'] = true;
            }
            setFilters(initialFilters);
        }
    }, [useCases]);

    const handleViewDetails = (bookingId: string, bookingNumber: string) => {
        dispatch(
            setBreadcrumbPathAndBookingNumber({
                bookingNumber: bookingNumber,
                path: location.pathname,
            }),
        );
        navigate(makeRentalBookingDetailsUrl(bookingId));
    };

    const handleViewSeriesDetails = (seriesId: string) => {
        navigate(makeSeriesBookingDetailsUrl(seriesId));
    };

    const getActiveServiceFilter = (): string | undefined => {
        const activeFilters = Object.entries(filters)
            .filter(([key, value]) => key !== 'all' && value)
            .map(([key]) => key);
        if (activeFilters.length === 1) return activeFilters[0];
        return undefined;
    };

    const exportBookings = async (toExport: Booking[], dateFrom?: string, dateTo?: string) => {
        setExportLoading(true);
        try {
            const result = await exportBookingsToCSV(toExport, t, i18n.language, {
                tenant: selectedTenant ?? 'kolula',
                serviceFilter: getActiveServiceFilter(),
                dateFrom,
                dateTo,
            });
            if (result.failedDetailCount > 0) {
                dispatch(enqueueWarningSnackbar({
                    message: t('bookings.bookings_table.csv.export_partial_warning', {
                        failed: result.failedDetailCount,
                        total: result.totalCount,
                    }),
                }));
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('CSV export failed:', error);
            dispatch(enqueueErrorSnackbar({
                message: t('bookings.bookings_table.csv.export_error'),
            }));
        } finally {
            setExportLoading(false);
        }
    };

    const handleExportCSV = async () => {
        setExportDialogOpen(false);
        await exportBookings(filteredBookings.bookings, exportDateFrom || undefined, exportDateTo || undefined);
        setExportDateFrom('');
        setExportDateTo('');
    };

    // The selection is exported as it is: no date range, the rows were picked.
    const handleExportSelected = () => exportBookings(selectedBookings);

    const toggleChecklist = () => setShowChecklist((prev) => !prev);

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = event.target;

        const updatedFilters = { ...filters };

        if (name === 'all') {
            useCases?.forEach((uc: string) => {
                updatedFilters[uc] = checked;
            });
            updatedFilters.all = checked;
        } else {
            updatedFilters[name] = checked;

            if (useCases && useCases.length > 0) {
                updatedFilters.all = useCases.every((uc: string) => updatedFilters[uc]);
            }
        }

        setFilters(updatedFilters);
        setPage(0);
    };

    return (
        <Container className={'px-0 sm:px-4'} maxWidth={false}>
            <Box sx={{ py: 3 }}>
                <PageHeader>
                    <PageHeadline>
                        {t('bookings.bookings_table.title')}
                    </PageHeadline>
                    <PageSubtitle>
                        {t('bookings.bookings_table.count', {
                            count: filteredBookings.totalFilteredRows,
                        })}
                    </PageSubtitle>
                    <SearchBar
                        className={'hidden sm:block'}
                        placeholder={searchPlaceholder}
                        value={searchQuery}
                        onChange={setSearchQuery}
                    />
                    {hasCreateBookingsPermission && (
                        <Box sx={{ flexShrink: 0 }}>
                            {noAssetForBooking ? (
                                <Tooltip
                                    title={t('bookings.bookings_table.no_asset_for_booking')}
                                    arrow
                                >
                                    {/* span wrapper required: MUI Tooltip can't
                                        attach to a disabled child directly */}
                                    <span>
                                        <CustomButton
                                            title={t('bookings.bookings_table.create_button')}
                                            isFilled={true}
                                            disabled={true}
                                            onClick={() => {}}
                                        />
                                    </span>
                                </Tooltip>
                            ) : (
                                <DropdownMenu
                                    trigger={
                                        <CustomButton
                                            title={t('bookings.bookings_table.create_button')}
                                            isFilled={true}
                                            onClick={() => {}}
                                        />
                                    }
                                    items={createBookingMenuItems}
                                />
                            )}
                        </Box>
                    )}
                </PageHeader>
                {viewTabs}
                {/* Search moves to its own full-width row on mobile, where it
                    doesn't fit alongside the title + create button */}
                <SearchBar
                    className={'block sm:hidden mb-3'}
                    placeholder={searchPlaceholder}
                    value={searchQuery}
                    onChange={setSearchQuery}
                />
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: { xs: 'flex-start', sm: 'space-between' },
                        alignItems: { xs: 'stretch', sm: 'center' },
                        gap: '8px',
                        mb: 3,
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: '#fff',
                            borderRadius: '8px',
                            '& fieldset': {
                                borderColor: '#E5E7EB',
                            },
                            '&:hover fieldset': {
                                borderColor: '#E5E7EB',
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: '#E5E7EB',
                            },
                        },
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: '8px',
                        }}
                    >   
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'row', sm: 'row' },
                                gap: '8px',
                                width: { xs: '100%', sm: 'auto' },
                                position: 'relative',
                            }}
                        >
                            <Button
                                variant={'outlined'}
                                startIcon={<FilterListIcon sx={{ color: '#6B7280' }} />}
                                onClick={toggleChecklist}
                                sx={{
                                    height: '40px',
                                    borderColor: '#E5E7EB',
                                    color: '#374151',
                                    backgroundColor: '#fff',
                                    textTransform: 'none',
                                    width: { xs: '100%', sm: 'auto' },
                                    '&:hover': {
                                        backgroundColor: '#F9FAFB',
                                        borderColor: '#E5E7EB',
                                    },
                                }}
                            >
                                {t('bookings.bookings_table.filter')}
                            </Button>
                            <Button
                                variant={'outlined'}
                                startIcon={exportLoading
                                    ? <CircularProgress size={18} sx={{ color: '#6B7280' }} />
                                    : <FileDownloadOutlinedIcon sx={{ color: '#6B7280' }} />
                                }
                                onClick={() => setExportDialogOpen(true)}
                                disabled={loading || exportLoading || filteredBookings.bookings.length === 0}
                                sx={{
                                    height: '40px',
                                    borderColor: '#E5E7EB',
                                    color: '#374151',
                                    backgroundColor: '#fff',
                                    textTransform: 'none',
                                    width: { xs: '100%', sm: 'auto' },
                                    '&:hover': {
                                        backgroundColor: '#F9FAFB',
                                        borderColor: '#E5E7EB',
                                    },
                                }}
                            >
                                {t('bookings.bookings_table.export_csv')}
                            </Button>
                            {useCases && showChecklist && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: '40px',
                                        left: 0,
                                        zIndex: 10,
                                        border: '1px solid #E5E7EB',
                                        borderRadius: '4px',
                                        padding: '8px',
                                        backgroundColor: '#fff',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                    }}
                                >
                                    <Typography variant={'caption'} sx={{ px: 1, pt: 1, pb: 0.5, display: 'block', color: '#6B7280' }}>
                                        {t('bookings.bookings_table.filter_by_service')}
                                    </Typography>
                                    <FormGroup>
                                        {/* Only show 'All' option when there are multiple services */}
                                        {useCases && useCases.length > 1 && (
                                            <FormControlLabel
                                                control={<Checkbox checked={filters.all || false} onChange={handleCheckboxChange} name={'all'} />}
                                                label={t('bookings.bookings_table.filter_options.all')}
                                            />
                                        )}
                                        {useCases?.map((useCase) => (
                                            <FormControlLabel
                                                key={useCase}
                                                control={
                                                    <Checkbox
                                                        checked={filters[useCase] || false}
                                                        onChange={handleCheckboxChange}
                                                        name={useCase}
                                                    />
                                                }
                                                label={SERVICE_LABELS[useCase] ?? useCase}
                                            />
                                        ))}
                                    </FormGroup>

                                    <Typography variant={'caption'} sx={{ px: 1, pt: 1, pb: 0.5, display: 'block', color: '#6B7280' }}>
                                        {t('bookings.bookings_table.filter_by_status')}
                                    </Typography>

                                    <FormGroup>
                                        {/* "All" toggle */}
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={statusFilters.all || false}
                                                    onChange={(_, checked) => {
                                                        // Derived from STATUS_KEYS rather than restated:
                                                        // the hand-written copy is how `overtime`
                                                        // came to be missing from one list and not
                                                        // the other (#2417 F1).
                                                        const next = STATUS_KEYS.reduce(
                                                            (acc, key) => ({ ...acc, [key]: checked }),
                                                            { all: checked } as Record<BookingStatusType | 'all', boolean>,
                                                        );
                                                        setStatusFilters(next);
                                                        setPage(0);
                                                    }}
                                                    name={'all-statuses'}
                                                />
                                            }
                                            label={t('bookings.bookings_table.filter_options.all')}
                                        />

                                        {STATUS_KEYS.map((s) => {
                                            return (
                                                <FormControlLabel
                                                    key={s}
                                                    control={
                                                        <Checkbox
                                                            checked={statusFilters[s] || false}
                                                            onChange={(_, checked) => {
                                                                const next = { ...statusFilters, [s]: checked };
                                                                next.all = STATUS_KEYS.every((k) => next[k]);
                                                                setStatusFilters(next);
                                                                setPage(0);
                                                            }}
                                                            name={s}
                                                        />
                                                    }
                                                    label={
                                                        {
                                                            active: t('booking_details.status.active'),
                                                            booked: t('booking_details.status.booked'),
                                                            canceled: t('booking_details.status.canceled'),
                                                            finished: t('booking_details.status.finished'),
                                                            reserved: t('booking_details.status.reserved'),
                                                            incompleted: t('booking_details.status.incompleted'),
                                                            blocked: t('booking_details.status.blocked'),
                                                            conflicted: t('booking_details.status.conflicted'),
                                                            expired: t('booking_details.status_badge.booking_status.expired'),
                                                            overtime: t('booking_details.status.overtime'),
                                                        }[s] ?? s
                                                    }
                                                />
                                            );
                                        })}
                                    </FormGroup>

                                    {/* Theme token, not the `#6B7280` its two
                                        neighbours still carry — a new line does
                                        not get to add a raw gray hex. */}
                                    <Typography
                                        variant={'caption'}
                                        className={'text-foreground/60'}
                                        sx={{ px: 1, pt: 1, pb: 0.5, display: 'block' }}
                                    >
                                        {t('bookings.bookings_table.filter_by_overtime')}
                                    </Typography>

                                    <FormGroup>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={overtimeOnly}
                                                    onChange={(_, checked) => {
                                                        setOvertimeOnly(checked);
                                                        setPage(0);
                                                    }}
                                                    name={'overtime-only'}
                                                />
                                            }
                                            label={t('bookings.bookings_table.filter_options.overtime_only')}
                                        />
                                    </FormGroup>

                                    {(hasRoomUseCase || awaitingApprovalOnly) && (
                                        <>
                                            <Typography
                                                variant={'caption'}
                                                className={'text-foreground/60'}
                                                sx={{ px: 1, pt: 1, pb: 0.5, display: 'block' }}
                                            >
                                                {t('bookings.bookings_table.filter_by_approval')}
                                            </Typography>

                                            <FormGroup>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={awaitingApprovalOnly}
                                                            onChange={(_, checked) => {
                                                                setAwaitingApprovalOnly(checked);
                                                                setPage(0);
                                                            }}
                                                            name={'awaiting-approval-only'}
                                                        />
                                                    }
                                                    label={t('bookings.bookings_table.filter_options.awaiting_approval_only')}
                                                />
                                            </FormGroup>
                                        </>
                                    )}

                                    <Typography
                                        variant={'caption'}
                                        className={'text-foreground/60'}
                                        sx={{ px: 1, pt: 1, pb: 0.5, display: 'block' }}
                                    >
                                        {t('bookings.bookings_table.filter_by_day')}
                                    </Typography>

                                    <RadioGroup
                                        value={dayFilter ? dayFilterKey(dayFilter) : 'any'}
                                        onChange={(_, value) => {
                                            setDayFilter(DAY_FILTER_OPTIONS.find((o) => dayFilterKey(o) === value) ?? null);
                                            setPage(0);
                                        }}
                                        name={'day-filter'}
                                    >
                                        <FormControlLabel
                                            value={'any'}
                                            control={<Radio />}
                                            label={t('bookings.bookings_table.filter_options.any_day')}
                                        />
                                        {DAY_FILTER_OPTIONS.map((option) => (
                                            <FormControlLabel
                                                key={dayFilterKey(option)}
                                                value={dayFilterKey(option)}
                                                control={<Radio />}
                                                label={t(`bookings.bookings_table.filter_options.${dayFilterKey(option)}`)}
                                            />
                                        ))}
                                    </RadioGroup>

                                    <Typography variant={'caption'} sx={{ px: 1, pt: 1, pb: 0.5, display: 'block', color: '#6B7280' }}>
                                        {t('bookings.bookings_table.filter_by_archive')}
                                    </Typography>

                                    <FormGroup>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={includeArchived}
                                                    onChange={(_, checked) => {
                                                        setIncludeArchived(checked);
                                                        setPage(0);
                                                    }}
                                                    name={'include-archived'}
                                                />
                                            }
                                            label={t('bookings.archive.toggle')}
                                        />
                                    </FormGroup>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Box>

                {/* A failed fetch used to render as "0 Buchungen" — identical to a
                    tenant with no bookings, so nobody could tell an outage from an
                    empty list (CLAUDE.md, "A Failure Must Never Look Like an
                    Absence"). The table stays below as it is; the notice says it
                    cannot be trusted, and which cause it was. */}
                {loadError && !loading && (
                    <div
                        role={'alert'}
                        className={
                            'flex flex-col sm:flex-row sm:items-center gap-3 mb-4 px-4 py-3 '
                            + 'rounded-[5px] border border-foreground/10 bg-text-contrast text-sm text-foreground'
                        }
                    >
                        <AlertTriangle className={'w-4 h-4 shrink-0'} />
                        <div className={'flex-1 min-w-0'}>
                            <p className={'font-medium'}>{t('bookings.bookings_table.load_error.title')}</p>
                            <p className={'text-foreground/60'}>
                                {loadError.status === null
                                    ? t('bookings.bookings_table.load_error.no_response')
                                    : t('bookings.bookings_table.load_error.status', { status: loadError.status })}
                                {loadError.message ? ` (${loadError.message})` : ''}
                            </p>
                        </div>
                        <CustomButton
                            isFilled={false}
                            title={t('bookings.bookings_table.load_error.retry')}
                            onClick={() => { fetchBooking(); }}
                        />
                    </div>
                )}

                <BookingsBulkActions
                    selected={selectedBookings}
                    canEdit={hasEditBookingsPermission}
                    exportLoading={exportLoading}
                    onExport={handleExportSelected}
                    onClearSelection={() => setSelectedIds(new Set())}
                    onFinished={() => {
                        setSelectedIds(new Set());
                        fetchBooking();
                    }}
                />

                <BookingTable
                    entries={tableEntries}
                    selectedIds={selectedIds}
                    onToggleSelected={(bookingId) => setSelectedIds((prev) => toggleSelected(prev, bookingId))}
                    onToggleAllSelected={(bookingIds) => setSelectedIds((prev) => toggleAllVisible(bookingIds, prev))}
                    // Filtering by locker while the locker column is hidden
                    // would answer "which Fach?" with a list that never shows
                    // one. The column is normally off for tenants whose use
                    // cases have no lockers — where a locker query matches
                    // nothing anyway, so forcing it costs them nothing.
                    forceLockerColumn={parseLockerQuery(searchQuery) !== null}
                    loading={loading || !hasFetched}
                    onViewDetails={handleViewDetails}
                    onViewSeriesDetails={handleViewSeriesDetails}
                    onRefetch={() => { fetchBooking(); }}
                />
            </Box>
            <CsvDateRangeDialog
                open={exportDialogOpen}
                dateFrom={exportDateFrom}
                dateTo={exportDateTo}
                onDateFromChange={setExportDateFrom}
                onDateToChange={setExportDateTo}
                allPeriodsHint={t('bookings.bookings_table.csv.all_periods')}
                onCancel={() => {
                    setExportDialogOpen(false);
                    setExportDateFrom('');
                    setExportDateTo('');
                }}
                onExport={handleExportCSV}
            />
        </Container>
    );
};

export default BookingsPage;
